import React, { useState, useEffect } from "react";
import { states } from "../../../utils/enums";
import {
  AccountSchema,
  CommunitySchema,
  LoginProps,
} from "../../../data/types";
import { CustomAddImageBox } from "../../../components/AddImageBox";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  SignUpEmailSchema,
  SignUpCommunitySchema,
} from "../../../utils/validationSchemas";
import AuthCard from "../../../components/unauth/AuthCard";
import { PARTNER_LINKS } from "../../../components/unauth/partnerContent";

/** Merge validated business fields with any uploaded logo/cover files so form submit cannot wipe File objects. */
export const mergeSignupCommunity = (
  formData: CommunitySchema,
  existing: CommunitySchema
): CommunitySchema => ({
  ...existing,
  ...formData,
  logo: existing.logo,
  bgPhoto: existing.bgPhoto,
});

const SignUp: React.FC<LoginProps> = ({ toggleSignIn, registerBusiness }) => {
  const [account, updateAccount] = useState<AccountSchema>({});
  const [business, setBusiness] = useState<CommunitySchema>({});
  const [isCommunityForm, toggleCommunityForm] = useState<boolean>(false);

  const goToNext = async () => {
    // if (account && account.email && registerUser !== undefined){
    //     var user_to_register: AccountSchema = account
    //     registerUser(user_to_register)
    // }
    toggleCommunityForm(true);
  };

  const goBack = async () => {
    toggleCommunityForm(false);
  };

  const goToOTP = async (communityToRegister: CommunitySchema = business) => {
    const canRegister = Boolean(
      account &&
      account.email &&
      communityToRegister &&
      registerBusiness !== undefined
    );
    if (canRegister && registerBusiness) {
      registerBusiness(communityToRegister, account);
    }
  };

  useEffect(() => {
    document.title = "Partner Dashboard | Signup";
  }, []);

  return (
    <AuthCard
      title="Become a resource partner"
      // subtitle="We would ask you and your organization."
      className={isCommunityForm ? "max-w-[560px]" : ""}
      footNote={
        <>
          By creating an account you agree to our{" "}
          <a
            href={PARTNER_LINKS.terms}
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-primary-100 hover:text-primary-300"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href={PARTNER_LINKS.privacy}
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-primary-100 hover:text-primary-300"
          >
            Privacy Policy
          </a>
          .
        </>
      }
    >
      {isCommunityForm ? (
        <CommunitySignUp
          account={account}
          business={business}
          setBusiness={setBusiness}
          goBack={goBack}
          goToOTP={goToOTP}
        />
      ) : (
        <EmailSignUp
          account={account}
          updateAccount={updateAccount}
          goToNext={goToNext}
        />
      )}
      <p className="mt-3 text-center font-open-sans text-[13px] text-muted">
        <span className="text-ink">Already have an Account? </span>
        <button
          type="button"
          className="border-0 bg-transparent p-0 font-open-sans text-[13px] font-semibold text-primary-300 hover:text-primary-100"
          onClick={toggleSignIn}
        >
          Sign in
        </button>
      </p>
    </AuthCard>
  );
};

type EmailSignUpProps = {
  account: AccountSchema;
  updateAccount: React.SetStateAction<React.Dispatch<AccountSchema>>;
  goToNext: () => void;
};

const EmailSignUp: React.FC<EmailSignUpProps> = (props) => {
  // const [account, updateAccount] = useState<AccountSchema>({})
  var { account, updateAccount, goToNext } = props;

  const {
    register,
    handleSubmit,
    formState: { errors },
    // setValue,
  } = useForm({
    resolver: zodResolver(SignUpEmailSchema),
    defaultValues: {
      first_name: account.first_name || "",
      last_name: account.last_name || "",
      email: account.email || "",
    },
  });

  const onSubmit = (data: any) => {
    updateAccount(data);
    goToNext();
  };

  // const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   let { name, value } = e.target;
  //   e.preventDefault();
  //   setValue(name as keyof typeof SignUpEmailSchema._type, value);
  //   console.log(name, value);
  //   console.log(name as keyof typeof SignUpEmailSchema._type);
  //   updateAccount((prevAccount) => ({
  //     ...prevAccount,
  //     [name]: value,
  //   }));
  // };

  return (
    <>
      <p className="mb-2 text-center font-open-sans text-[16px] text-[#333333]">
        Enter Contact Information
      </p>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex-row max-w-[765px]"
      >
        <input
          placeholder="First Name"
          className="auth-input mb-2"
          {...register("first_name")}
        />
        {errors.first_name && (
          <span className="text-red-500 text-sm font-open-sans w-[98%] ml-1">
            {String(errors.first_name?.message)}
          </span>
        )}

        <input
          placeholder="Last Name"
          className="auth-input mb-2"
          {...register("last_name")}
        />
        {errors.last_name && (
          <span className="text-red-500 text-sm font-open-sans ml-1 w-[98%]">
            {errors.last_name.message as string}
          </span>
        )}

        <input
          placeholder="Email Address"
          className="auth-input mb-2"
          {...register("email")}
          onKeyDown={(e) =>
            e.key === "Enter" && account.email ? goToNext() : null
          }
        />
        {errors.email && (
          <span className="text-red-500 text-sm font-open-sans w-relative w-[98%] ml-1">
            {errors.email.message as string}
          </span>
        )}

        <button
          type="submit"
          className="auth-primary-btn mt-3"
        >
          NEXT
        </button>
      </form>
    </>
  );
};

type CommunitySignUpProps = {
  account: AccountSchema;
  business: CommunitySchema;
  setBusiness: React.Dispatch<React.SetStateAction<CommunitySchema>>;
  goBack: () => void;
  goToOTP: (communityToRegister?: CommunitySchema) => void;
};

const CommunitySignUp: React.FC<CommunitySignUpProps> = (props) => {
  var { account, business, setBusiness, goBack, goToOTP } = props;

  const [selectedState, setSelectedState] = useState<string>("");
  const [width, setWidth] = useState<string>("0");
  const [length, setLength] = useState<string>("0");
  const [selectedbgPhoto, setSelectedbgPhoto] = useState<string | undefined>(
    ""
  );
  const [selectedLogo, setSelectedLogo] = useState<string | undefined>("");

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    // watch,
  } = useForm({
    resolver: zodResolver(SignUpCommunitySchema),
    defaultValues: {
      name: business.name || "",
      description: business.description || "",
      address: business.address || "",
      city: business.city || "",
      state: Array.isArray(business.state)
        ? business.state[0] || ""
        : business.state || "",
      zipcode: business.zipcode || "",
      website: business.website || "",
      width: width,
      length: length,
      capacity: business.capacity || "",
      logo: undefined,
      bgPhoto: selectedbgPhoto || undefined,
      size: business.size || "",
    },
  });

  const onSubmit = (data: any) => {
    const mergedBusiness = mergeSignupCommunity(data, business);
    setBusiness(mergedBusiness);
    goToOTP(mergedBusiness);
  };

  const handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedState(event.target.value);
    setValue("state", event.target.value);
    setBusiness((prevKitchen) => ({
      ...prevKitchen,
      state: event.target.value,
    } as unknown as CommunitySchema));
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setValue(name as keyof typeof SignUpCommunitySchema._type, value);
    setBusiness((prevKitchen) => ({
      ...prevKitchen,
      [name]: value,
    }));
  };

  /** Keep react-hook-form's onChange while mirroring values into community state. */
  const bindTextField = (name: "name" | "description" | "address" | "city" | "zipcode" | "website" | "capacity") => {
    const field = register(name);
    return {
      ...field,
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        void field.onChange(event);
        handleInputChange(event);
      },
    };
  };

  const stateField = register("state");

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = event.target;
    if (name && files && files.length > 0) {
      let file_data = files[0];
      setBusiness((prevKitchen) => ({
        ...prevKitchen,
        [name]: file_data,
      }));
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // const file = event.target.files?.[0];
    const { name, files } = event.target;
    const file = files?.[0];
    if (file) {
      // Read the selected image file as a data URL
      const reader = new FileReader();
      reader.onload = (e) => {
        if (name === "logo") {
          setSelectedLogo(e.target?.result as string);
        } else if (name === "bgPhoto") {
          setSelectedbgPhoto(e.target?.result as string);
        }
      };
      reader.readAsDataURL(file);
    } else {
      if (name === "logo") {
        setSelectedLogo("");
      } else if (name === "bgPhoto") {
        setSelectedbgPhoto("");
      }
    }
    handleFileChange(event);
  };

  const handleLengthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setLength(value);
    setValue("length", value);
    setValue("size", `${width}x${value}`);
    setBusiness((prevKitchen) => ({
      ...prevKitchen,
      size: `${width}x${value}`,
    }));
  };

  const handleWidthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setWidth(value);
    setValue("width", value);
    setValue("size", `${value}x${length}`);
    setBusiness((prevKitchen) => ({
      ...prevKitchen,
      size: `${value}x${length}`,
    }));
  };

  return (
    <>
      <p className="mb-2 text-center font-open-sans text-[16px] text-[#333333]">
        Enter Business Information
      </p>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full grid grid-flow-row gap-y-5"
      >
        <div className="flex flex-col">
          <input
            placeholder="Business Name"
            className="font-open-sans text-[16px] h-[50px] w-full bg-transparent p-2 rounded-md border-[1px] border-[#E9E9E9] text-[#000000] text-start"
            {...bindTextField("name")}
          />
          {errors.name && (
            <span className="text-red-500 text-sm font-open-sans ml-1">
              {errors.name.message as string}
            </span>
          )}
        </div>

        <textarea
          placeholder="Description"
          className="font-open-sans text-[16px] h-[50px] w-full bg-transparent p-2 rounded-md border-[1px] border-[#E9E9E9] text-[#000000] text-start"
          {...bindTextField("description")}
        />
        {errors.description && (
          <span className="text-red-500 text-sm font-open-sans ml-1">
            {errors.description.message as string}
          </span>
        )}

        <div className="grid grid-cols-3 gap-x-5">
          <div className="flex flex-col col-span-2">
            <input
              placeholder="Address"
              className="font-open-sans text-[16px] h-[50px] w-full bg-transparent p-2 rounded-md border-[1px] border-[#E9E9E9] text-[#000000] text-start col-span-2"
              {...bindTextField("address")}
            />
            {errors.address && (
              <span className="text-red-500 text-sm font-open-sans ml-1">
                {errors.address.message as string}
              </span>
            )}
          </div>
          <div className="flex flex-col">
            <input
              placeholder="City"
              className="font-open-sans text-[16px] h-[50px] w-full bg-transparent p-2 rounded-md border-[1px] border-[#E9E9E9] text-[#000000] text-start"
              {...bindTextField("city")}
            />
            {errors.city && (
              <span className="text-red-500 text-sm font-open-sans ml-1">
                {errors.city.message as string}
              </span>
            )}
          </div>
          {/* <input
                    placeholder="County"
                    className="h-[50px] w-[98%] m-[1%] bg-transparent p-2 rounded-md border-2 border-[#013847] text-[#000000]  text-start"
                    name="county"
                    onChange={handleInputChange}
                /> */}
        </div>

        <div className="grid grid-cols-2 gap-x-5 w-full">
          <div className="flex flex-col">
            <select
              {...stateField}
              value={selectedState}
              onChange={(event) => {
                void stateField.onChange(event);
                handleStateChange(event);
              }}
              className="font-open-sans text-[16px] h-[50px] w-full bg-transparent p-2 rounded-md border-[1px] border-[#E9E9E9] text-start text-slate-500"
            >
              <option value="">Select a state</option>
              {Object.values(states).map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
            {errors.state && (
              <span className="text-red-500 text-sm font-open-sans ml-1">
                {errors.state.message as string}
              </span>
            )}
          </div>
          <div className="flex flex-col">
            <input
              placeholder="Zipcode"
              className="font-open-sans text-[16px] h-[50px] w-full bg-transparent p-2 rounded-md border-[1px] border-[#E9E9E9] text-[#000000] text-start"
              {...bindTextField("zipcode")}
            />
            {errors.zipcode && (
              <span className="text-red-500 text-sm font-open-sans ml-1">
                {errors.zipcode.message as string}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col">
          <input
            placeholder="Website"
            className="font-open-sans text-[16px] h-[50px] w-full bg-transparent p-2 rounded-md border-[1px] border-[#E9E9E9] text-[#000000] text-start"
            {...bindTextField("website")}
          />
          {errors.website && (
            <span className="text-red-500 text-sm font-open-sans ml-1">
              {errors.website.message as string}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-x-5">
          <div className="grid grid-cols-2 gap-x-5">
            <input
              placeholder="Width"
              className="font-open-sans text-[16px] h-[50px] w-full bg-transparent p-2 rounded-md border-[1px] border-[#E9E9E9] text-[#000000] text-start"
              value={width}
              onChange={handleWidthChange}
            />
            <input
              placeholder="Length"
              className="font-open-sans text-[16px] h-[50px] w-full bg-transparent p-2 rounded-md border-[1px] border-[#E9E9E9] text-[#000000] text-start"
              value={length}
              onChange={handleLengthChange}
            />
          </div>
          <input
            placeholder="Max Capacity"
            className="font-open-sans text-[16px] h-[50px] w-full bg-transparent p-2 rounded-md border-[1px] border-[#E9E9E9] text-[#000000] text-start"
            {...bindTextField("capacity")}
          />
        </div>

        <div className="flex flex-row text-sm justify-between font-open-sans">
          <div className="flex flex-col md:flex-row w-full text-[#000] border-[1px] rounded-lg border-[#E9E9E9] ">
            <div className="flex flex-col justify-start my-4 mx-auto">
              <p className="text-[14px] text-[#4D4D4D] font-open-sans w-auto mt-1 mx-4 font-medium">
                Add business logo.
              </p>
              <CustomAddImageBox
                name="logo"
                height="70px"
                width="150px"
                selectedImage={selectedLogo}
                setSelectedImage={setSelectedLogo}
                handleChange={handleImageChange}
              />
              <p className="text-[12px] text-[#4D4D4D] font-open-sans w-auto mt-1 mx-2 font-light">
                Ensure the logo is square and at least 200x200 pixels (JPEG or
                PNG format).
              </p>
            </div>

            <div className="flex flex-col justify-start my-4  mx-4 md:mx-auto border-0 md:border-l-[1px] border-[#E9E9E9]">
              <p className="text-[14px] text-[#4D4D4D] font-open-sans w-auto md:w-[200px] mt-1 mx-4 font-medium">
                Add photo to represent business.
              </p>
              <CustomAddImageBox
                name="bgPhoto"
                height="70px"
                width="150px"
                selectedImage={selectedbgPhoto}
                setSelectedImage={setSelectedbgPhoto}
                handleChange={handleImageChange}
              />
              <p className="text-[12px] text-[#4D4D4D] font-open-sans md:w-[200px] mt-1 mx-2 font-light">
                Ensure the image is square and at least 200x200 pixels (JPEG or
                PNG format).
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-row w-full justify-evenly">
          <button
            type="button"
            className="bg-[#ddd] text-[#777] hover:bg-[#777] hover:text-[#eee]  m-3 border-0 font-open-sans h-10 w-full rounded-md"
            onClick={() => goBack()}
          >
            BACK
          </button>
          <button
            type="submit"
            className="auth-primary-btn m-3"
            disabled={!(account && account.email)}
          >
            SIGN UP
          </button>
        </div>
      </form>
    </>
  );
};

export default SignUp;
